import React, { useState, useEffect } from "react";
import {
  EuiForm,
  EuiFormRow,
  EuiFieldText,
  EuiSelect,
  EuiButton,
  EuiText,
  EuiInlineEditTitle,
  EuiFlexGroup,
  EuiFlexItem,
} from "@elastic/eui";
import { PostOptions } from "../utils/RequestUtils";
import { getApiUrl } from "../utils/api";

const EditAgentForm = ({ config, course, onNameChange, onSettingsUpdate }) => {
  const [form, setForm] = useState({
    config_name: config.config_name,
    course_name: course.course_name,
    course_number: course.course_number,
  });
  const [settings, setSettings] = useState({
    mode: config.mode || "step-by-step",
    default_assistance_level: config.default_assistance_level || 1,
  });
  const [preName, setPreName] = useState(config.config_name);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    setForm({
      config_name: config.config_name,
      course_name: course.course_name,
      course_number: course.course_number,
    });
    setSettings({
      mode: config.mode || "step-by-step",
      default_assistance_level: config.default_assistance_level || 1,
    });
    setPreName(config.config_name);
  }, [config, course.course_name, course.course_number]);

  const sendUpdateRequest = async (url, payload) => {
    const response = await fetch(url, PostOptions(payload));
    const text = await response.json();
    if (!response.ok) {
      throw new Error(text.message || text.error || "Request failed");
    }
    return text;
  };

  const changeName = async () => {
    try {
      setIsRenaming(true);
      setErrorMessage(null);
      await sendUpdateRequest(getApiUrl("/api/agent/name/edit"), {
        config_name: preName,
        course_name: course.course_name,
        new_name: form.config_name,
      });
      onNameChange(preName, form.config_name);
      setPreName(form.config_name);
    } catch (error) {
      console.error("There was an error!", error);
      setErrorMessage(error.message);
    } finally {
      setIsRenaming(false);
    }
  };

  const toggleSettingsEditor = () => {
    if (isEditingSettings) {
      setSettings({
        mode: config.mode || "step-by-step",
        default_assistance_level: config.default_assistance_level || 1,
      });
    }
    setIsEditingSettings((prev) => !prev);
  };

  const saveAgentSettings = async () => {
    try {
      setIsSavingSettings(true);
      setErrorMessage(null);
      await sendUpdateRequest(getApiUrl("/api/agent/mode/edit"), {
        config_name: form.config_name,
        course_name: course.course_name,
        mode: settings.mode,
      });
      await sendUpdateRequest(getApiUrl("/api/agent/assistance-level/edit"), {
        config_name: form.config_name,
        course_name: course.course_name,
        default_assistance_level: settings.default_assistance_level,
      });
      onSettingsUpdate?.({
        mode: settings.mode,
        default_assistance_level: settings.default_assistance_level,
      });
      setIsEditingSettings(false);
    } catch (error) {
      console.error("Failed to update agent settings", error);
      setErrorMessage(error.message);
    } finally {
      setIsSavingSettings(false);
    }
  };

  return (
    <EuiFlexGroup direction="column" gutterSize="xs">
      <EuiFlexItem>
        <EuiInlineEditTitle
          inputAriaLabel="Edit title inline"
          value={form.config_name}
          onChange={(e) => setForm({ ...form, config_name: e.target.value })}
          onSave={changeName}
          onCancel={() => setForm({ ...form, config_name: preName })}
          isLoading={isRenaming}
          heading="h3"
          size="s"
        />
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiButton
          size="s"
          onClick={toggleSettingsEditor}
          iconType={isEditingSettings ? "cross" : "pencil"}
          color="primary"
        >
          {isEditingSettings ? "Close Agent Settings" : "Edit Agent Settings"}
        </EuiButton>
      </EuiFlexItem>
      {isEditingSettings && (
        <EuiFlexItem>
          <EuiForm
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              saveAgentSettings();
            }}
          >
            <EuiFormRow label="Default AI Assistance Level">
              <EuiSelect
                options={[
                  { value: 1, text: "Level 1 - Idea Spark (no code)" },
                  { value: 2, text: "Level 2 - Code Check (review only)" },
                  { value: 3, text: "Level 3 - Debug Helper (trace errors)" },
                  { value: 4, text: "Level 4 - Co-Builder (draft snippets)" },
                  { value: 5, text: "Level 5 - AI Only (full solutions)" },
                ]}
                value={settings.default_assistance_level}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    default_assistance_level: parseInt(e.target.value, 10),
                  })
                }
                disabled={isSavingSettings}
              />
            </EuiFormRow>
            <EuiButton
              fill
              type="submit"
              isLoading={isSavingSettings}
              disabled={isSavingSettings}
            >
              Save Settings
            </EuiButton>
          </EuiForm>
        </EuiFlexItem>
      )}
      <EuiFlexItem>
        <EuiText color="danger">{errorMessage}</EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};

export default EditAgentForm;
